import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Key, Copy, Trash2, Webhook, BookOpen, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/utils/api';
import { useAuth } from '@/context/AuthContext';

export default function VendorAPIPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [apiKeys, setApiKeys] = useState([]);
  const [webhooks, setWebhooks] = useState([]);
  const [showWebhookDialog, setShowWebhookDialog] = useState(false);
  const [showDocsDialog, setShowDocsDialog] = useState(false);

  useEffect(() => {
    if (!user || user.role !== 'vendor') {
      navigate('/');
      return;
    }
    fetchApiKeys();
    fetchWebhooks();
  }, [user]);

  const fetchApiKeys = async () => {
    try {
      const response = await api.getVendorApiKeys();
      setApiKeys(response.data);
    } catch (error) {
      console.error('Failed to fetch API keys:', error);
    }
  };

  const fetchWebhooks = async () => {
    try {
      const response = await api.getVendorWebhooks();
      setWebhooks(response.data);
    } catch (error) {
      console.error('Failed to fetch webhooks:', error);
    }
  };

  const generateApiKey = async () => {
    try {
      const response = await api.generateVendorApiKey();
      toast.success('API key generated successfully!');
      fetchApiKeys();
    } catch (error) {
      toast.error('Failed to generate API key');
    }
  };

  const revokeApiKey = async (keyId) => {
    try {
      await api.revokeVendorApiKey(keyId);
      toast.success('API key revoked');
      fetchApiKeys();
    } catch (error) {
      toast.error('Failed to revoke API key');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const handleConfigureWebhook = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    try {
      await api.configureVendorWebhook({
        url: formData.get('webhook_url'),
        events: ['booking.created', 'booking.updated', 'booking.cancelled']
      });
      toast.success('Webhook configured successfully!');
      setShowWebhookDialog(false);
      fetchWebhooks();
    } catch (error) {
      toast.error('Failed to configure webhook');
    }
  };

  const apiEndpoints = [
    {
      method: 'POST',
      endpoint: '/api/v1/properties',
      description: 'Create a new property',
      example: {
        name: "Grand Hotel Mumbai",
        location: "Nariman Point",
        city: "Mumbai",
        capacity: 500,
        price_per_day: 250000,
        amenities: ["WiFi", "Parking", "Pool"],
        images: ["https://..."],
        description: "Luxury hotel...",
        property_type: "hotel"
      }
    },
    {
      method: 'GET',
      endpoint: '/api/v1/properties',
      description: 'Get all your properties',
      example: null
    },
    {
      method: 'PATCH',
      endpoint: '/api/v1/properties/{property_id}',
      description: 'Update property details',
      example: {
        price_per_day: 300000,
        capacity: 600
      }
    },
    {
      method: 'DELETE',
      endpoint: '/api/v1/properties/{property_id}',
      description: 'Delete a property',
      example: null
    },
    {
      method: 'GET',
      endpoint: '/api/v1/bookings',
      description: 'Get bookings for your properties',
      example: null
    },
    {
      method: 'PATCH',
      endpoint: '/api/v1/bookings/{booking_id}/status',
      description: 'Update booking status',
      example: {
        status: "confirmed"
      }
    }
  ];

  return (
    <div className="min-h-screen pt-24 pb-20 px-6 bg-slate-50">
      <div className="max-w-7xl mx-auto">
        <div className="mb-12">
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-navy mb-4" data-testid="vendor-api-title">
            API Integration
          </h1>
          <p className="text-lg text-slate-600">
            Integrate your property management system with My Corporate Hub's API
          </p>
        </div>

        {/* Alert Banner */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-8 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-900">Keep your API keys secure</p>
            <p className="text-sm text-amber-700">Never share your API keys publicly or commit them to version control</p>
          </div>
        </div>

        {/* API Keys Section */}
        <div className="bg-white p-8 rounded-sm border border-slate-200 mb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Key className="w-6 h-6 text-navy" />
              <h2 className="text-2xl font-bold text-navy">API Keys</h2>
            </div>
            <Button 
              onClick={generateApiKey}
              className="rounded-sm bg-navy hover:bg-navy-800"
              data-testid="generate-api-key-button"
            >
              Generate New Key
            </Button>
          </div>

          {apiKeys.length === 0 ? (
            <div className="text-center py-12 border border-dashed border-slate-300 rounded-sm">
              <Key className="w-12 h-12 mx-auto mb-4 text-slate-400" />
              <p className="text-slate-600 mb-4">No API keys generated yet</p>
              <Button onClick={generateApiKey} variant="outline" className="border-navy text-navy hover:bg-navy hover:text-white">
                Generate Your First API Key
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {apiKeys.map((key) => (
                <div 
                  key={key.id} 
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-sm border border-slate-200"
                  data-testid={`api-key-${key.id}`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <code className="text-sm font-mono bg-white px-3 py-1 rounded border border-slate-200 text-slate-800">
                        {key.api_key}
                      </code>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => copyToClipboard(key.api_key)}
                        data-testid={`copy-key-${key.id}`}
                        className="text-navy hover:text-gold"
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="text-xs text-slate-600">
                      <span>Created: {new Date(key.created_at).toLocaleDateString()}</span>
                      <span className="mx-2">•</span>
                      <span>Used {key.usage_count} times</span>
                      {key.last_used && (
                        <>
                          <span className="mx-2">•</span>
                          <span>Last used: {new Date(key.last_used).toLocaleDateString()}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => revokeApiKey(key.id)}
                    className="text-red-600 hover:text-red-700 border-red-300 hover:bg-red-50"
                    data-testid={`revoke-key-${key.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Webhooks Section */}
        <div className="bg-white p-8 rounded-sm border border-slate-200 mb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Webhook className="w-6 h-6 text-navy" />
              <h2 className="text-2xl font-bold text-navy">Webhooks</h2>
            </div>
            <Button 
              onClick={() => setShowWebhookDialog(true)}
              className="rounded-sm bg-navy hover:bg-navy-800"
              data-testid="configure-webhook-button"
            >
              Configure Webhook
            </Button>
          </div>

          <p className="text-sm text-slate-600 mb-6">
            Receive real-time notifications when bookings are created, updated, or cancelled
          </p>

          {webhooks.length === 0 ? (
            <div className="text-center py-12 border border-dashed border-slate-300 rounded-sm">
              <Webhook className="w-12 h-12 mx-auto mb-4 text-slate-400" />
              <p className="text-slate-600 mb-4">No webhooks configured</p>
            </div>
          ) : (
            <div className="space-y-4">
              {webhooks.map((webhook) => (
                <div key={webhook.id} className="p-4 bg-slate-50 rounded-sm border border-slate-200">
                  <div className="flex items-center justify-between mb-2">
                    <code className="text-sm font-mono text-slate-800">{webhook.url}</code>
                    <span className="text-xs px-2 py-1 rounded-sm bg-emerald-100 text-emerald-800 font-semibold">
                      Active
                    </span>
                  </div>
                  <div className="text-xs text-slate-600">
                    Events: {webhook.events.join(', ')}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* API Documentation */}
        <div className="bg-white p-8 rounded-lg border border-slate-200">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <BookOpen className="w-6 h-6 text-navy" />
              <h2 className="text-2xl font-bold text-navy">API Documentation</h2>
            </div>
            <Button 
              onClick={() => setShowDocsDialog(true)}
              variant="outline"
              className="rounded-lg border-navy text-navy hover:bg-navy hover:text-white"
              data-testid="view-docs-button"
            >
              View Full Documentation
            </Button>
          </div>

          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-slate-800 mb-3">Authentication</h3>
              <p className="text-sm text-slate-600 mb-2">
                Include your API key in the request header:
              </p>
              <code className="block text-sm bg-slate-900 text-slate-100 p-4 rounded-lg font-mono">
                X-API-Key: your_api_key_here
              </code>
            </div>

            <div>
              <h3 className="font-semibold text-slate-800 mb-3">Base URL</h3>
              <code className="text-sm bg-slate-100 px-3 py-2 rounded-lg font-mono text-slate-700 inline-block">
                {process.env.REACT_APP_BACKEND_URL}
              </code>
            </div>

            <div>
              <h3 className="font-semibold text-slate-800 mb-4">Available Endpoints</h3>
              <div className="space-y-4">
                {apiEndpoints.slice(0, 3).map((endpoint, idx) => (
                  <div key={idx} className="border border-slate-200 rounded-lg p-4 hover:border-slate-300 transition-colors">
                    <div className="flex items-center gap-3 mb-2">
                      <span className={`text-xs font-bold px-2 py-1 rounded ${
                        endpoint.method === 'GET' ? 'bg-blue-100 text-blue-800' :
                        endpoint.method === 'POST' ? 'bg-green-100 text-green-800' :
                        endpoint.method === 'PATCH' ? 'bg-amber-100 text-amber-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {endpoint.method}
                      </span>
                      <code className="text-sm font-mono text-slate-700">{endpoint.endpoint}</code>
                    </div>
                    <p className="text-sm text-slate-600">{endpoint.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Webhook Configuration Dialog */}
      <Dialog open={showWebhookDialog} onOpenChange={setShowWebhookDialog}>
        <DialogContent data-testid="webhook-dialog">
          <DialogHeader>
            <DialogTitle>Configure Webhook</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleConfigureWebhook} className="space-y-4">
            <div>
              <label className="text-sm font-medium">Webhook URL</label>
              <Input 
                name="webhook_url" 
                placeholder="https://your-domain.com/webhook" 
                required 
                data-testid="webhook-url-input"
              />
              <p className="text-xs text-slate-800 mt-1">
                We'll send POST requests to this URL when events occur
              </p>
            </div>
            <Button type="submit" className="w-full rounded-sm bg-midnight-navy" data-testid="save-webhook-button">
              Save Webhook
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* API Documentation Dialog */}
      <Dialog open={showDocsDialog} onOpenChange={setShowDocsDialog}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="luxury-text text-2xl">Complete API Documentation</DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            {apiEndpoints.map((endpoint, idx) => (
              <div key={idx} className="border border-slate-200 rounded-sm p-4">
                <div className="flex items-center gap-3 mb-3">
                  <span className={`text-xs font-bold px-2 py-1 rounded ${
                    endpoint.method === 'GET' ? 'bg-blue-100 text-blue-800' :
                    endpoint.method === 'POST' ? 'bg-green-100 text-green-800' :
                    endpoint.method === 'PATCH' ? 'bg-amber-100 text-amber-800' :
                    'bg-red-100 text-red-800'
                  }`}>
                    {endpoint.method}
                  </span>
                  <code className="text-sm font-mono font-semibold">{endpoint.endpoint}</code>
                </div>
                <p className="text-sm text-slate-800 mb-3">{endpoint.description}</p>
                {endpoint.example && (
                  <div>
                    <p className="text-xs font-semibold mb-2">Request Body:</p>
                    <pre className="text-xs bg-slate-900 text-slate-100 p-3 rounded overflow-x-auto">
                      {JSON.stringify(endpoint.example, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
